import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@common/components';
import { useModal } from '@common/components/hooks';
import { GENETIC_COUNSELING_TRANSLATION } from '@modules/genetic-counseling/constants';

import { StyledConsentedCheck, StyledConsentedChecks, StyledSuccessIcon } from './UpcomingAppointmentCard.styles';
import { UpcomingAppointmentCardProps } from './UpcomingAppointmentCard.types';
import PatientProfileOverviewCard from '../PatientProfileOverviewCard/PatientProfileOverviewCard';
import { MainInfo, OverviewAction } from '../PatientProfileOverviewCard/PatientProfileOverviewCard.types';
import CancelAppointmentModal from '../../CancelAppointmentModal/CancelAppointmentModal';
import { useBookAppointmentContext } from '@/modules/genetic-counseling/hooks';

const UpcomingAppointmentCard: FC<UpcomingAppointmentCardProps> = ({ appointmentInfo }) => {
  const { t } = useTranslation([GENETIC_COUNSELING_TRANSLATION]);
  const { isShowing: isShowingCancelApptModal, toggle: toggleCancelAppointmentModal } = useModal();
  const bookAppointmentContext = useBookAppointmentContext();

  const appointmentInfoData: MainInfo[] = [
    {
      id: 'appt-date',
      label: t('upcoming.counseling_status_card.appointment_date'),
      value: appointmentInfo?.date,
    },
    {
      id: 'appt-time',
      label: t('upcoming.counseling_status_card.appointment_time'),
      value: appointmentInfo?.time,
    },
    {
      id: 'appt-type',
      label: t('upcoming.counseling_status_card.appointment_type'),
      value: appointmentInfo?.type,
    },
  ];

  const consentedChecks = [
    t('upcoming.counseling_status_card.check_consented_counseling'),
    t('upcoming.counseling_status_card.check_consented_share'),
  ];

  const handleRescheduleAppointment = (): void => {
    bookAppointmentContext?.handleRescheduleAppointment?.();
  };
  const handleCancelAppointment = (): void => {
    toggleCancelAppointmentModal();
  };

  const actions: OverviewAction[] = [];
  actions.push({
    id: 'reschedule',
    label: t('reschedule_appointment.title'),
    onClick: handleRescheduleAppointment,
  });
  actions.push({
    id: 'cancel',
    label: t('cancel_appointment.title'),
    onClick: handleCancelAppointment,
  });

  return (
    <>
      <PatientProfileOverviewCard
        title={t('upcoming.counseling_status_card.title')}
        actions={actions}
        mainInfo={appointmentInfoData}
        notes={appointmentInfo?.notes}
      >
        <StyledConsentedChecks>
          {consentedChecks.map((item, index) => (
            <StyledConsentedCheck key={index}>
              <StyledSuccessIcon />
              <Box data-testid={`upcoming-appt-checks-${index}`}>{item}</Box>
            </StyledConsentedCheck>
          ))}
        </StyledConsentedChecks>
      </PatientProfileOverviewCard>
      {isShowingCancelApptModal && appointmentInfo?.id && (
        <CancelAppointmentModal
          id={appointmentInfo?.id}
          isOpen={isShowingCancelApptModal}
          onClose={() => toggleCancelAppointmentModal()}
        />
      )}
    </>
  );
};

export default UpcomingAppointmentCard;
